import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const D2 = (v) => Number(Number(v || 0).toFixed(2));

function formatFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function RentabilidadPanel() {
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [expandido, setExpandido] = useState(null);
  const [editando, setEditando] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(30);

  const cargar = () => {
    setCargando(true);
    api.get('/liquidaciones/')
      .then((res) => {
        const data = res.data.results || res.data;
        setLiquidaciones(data.filter((l) => !l.archivado));
      })
      .catch(() => toast.error('No se pudieron cargar las liquidaciones.'))
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => setFiltroTexto(busqueda.trim().toLowerCase()), 350);
    return () => clearTimeout(timer);
  }, [busqueda]);

  useEffect(() => setPagina(1), [filtroTexto]);

  const filtradas = useMemo(() => {
    if (!filtroTexto) return liquidaciones;
    return liquidaciones.filter((l) => {
      const nombre = (l.cliente_nombre || '').toLowerCase();
      const guia = (l.guia_display || '').toLowerCase();
      const casillero = (l.cliente_detalle?.codigo_casillero || '').toLowerCase();
      return nombre.includes(filtroTexto) || guia.includes(filtroTexto) || casillero.includes(filtroTexto);
    });
  }, [liquidaciones, filtroTexto]);

  const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const paginadas = filtradas.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);

  const totales = useMemo(() => {
    let ingresos = 0, costos = 0, utilidad = 0;
    for (const l of filtradas) {
      ingresos += D2(l.total);
      const ci = l.costo_interno;
      if (ci) {
        costos += D2(ci.total_costo);
        utilidad += D2(ci.utilidad);
      }
    }
    const margen = ingresos > 0 ? (utilidad / ingresos) * 100 : 0;
    return { ingresos: D2(ingresos), costos: D2(costos), utilidad: D2(utilidad), margen: D2(margen) };
  }, [filtradas]);

  const toggleExpandir = (id) => {
    if (expandido === id) {
      setExpandido(null);
      setEditando({});
    } else {
      setExpandido(id);
      const liq = liquidaciones.find((l) => l.id === id);
      const ci = liq?.costo_interno || {};
      setEditando({
        costo_mtc: D2(ci.costo_mtc),
        ultima_milla: D2(ci.ultima_milla),
        flete_volumetrico: D2(ci.flete_volumetrico),
        consolidacion_costo: D2(ci.consolidacion_costo),
        reembolso_aduana_costo: D2(ci.reembolso_aduana_costo),
        reembolso_gastos_bancarios_costo: D2(ci.reembolso_gastos_bancarios_costo),
        fac_mtc_servicio: ci.fac_mtc_servicio || '',
        fac_mtc_reembolso: ci.fac_mtc_reembolso || '',
        fac_ecuafly_servicio: ci.fac_ecuafly_servicio || '',
        fac_ecuafly_reembolso: ci.fac_ecuafly_reembolso || '',
        forma_cobro_interna: ci.forma_cobro_interna || '',
        notas: ci.notas || '',
        _id: ci.id || null,
      });
    }
  };

  const guardarCostos = async (liqId) => {
    setGuardando(true);
    try {
      const liq = liquidaciones.find((l) => l.id === liqId);
      const paqueteIds = liq?.paquetes || [];
      if (paqueteIds.length === 0) {
        toast.error('No hay paquetes asociados.');
        return;
      }

      const totalCosto = D2(
        Number(editando.costo_mtc || 0) + Number(editando.ultima_milla || 0) +
        Number(editando.flete_volumetrico || 0) + Number(editando.consolidacion_costo || 0) +
        Number(editando.reembolso_aduana_costo || 0) + Number(editando.reembolso_gastos_bancarios_costo || 0)
      );
      const totalIngreso = D2(liq.total);
      const utilidad = D2(totalIngreso - totalCosto);
      const margen = totalIngreso > 0 ? D2((utilidad / totalIngreso) * 100) : 0;

      const payload = {
        paquete: paqueteIds[0],
        liquidacion: liqId,
        costo_mtc: editando.costo_mtc || 0,
        ultima_milla: editando.ultima_milla || 0,
        flete_volumetrico: editando.flete_volumetrico || 0,
        consolidacion_costo: editando.consolidacion_costo || 0,
        reembolso_aduana_costo: editando.reembolso_aduana_costo || 0,
        reembolso_gastos_bancarios_costo: editando.reembolso_gastos_bancarios_costo || 0,
        total_costo: totalCosto,
        utilidad,
        margen_pct: margen,
        fac_mtc_servicio: editando.fac_mtc_servicio,
        fac_mtc_reembolso: editando.fac_mtc_reembolso,
        fac_ecuafly_servicio: editando.fac_ecuafly_servicio,
        fac_ecuafly_reembolso: editando.fac_ecuafly_reembolso,
        forma_cobro_interna: editando.forma_cobro_interna,
        notas: editando.notas,
      };

      let res;
      if (editando._id) {
        res = await api.patch(`/costos-internos/${editando._id}/`, payload);
      } else {
        res = await api.post('/costos-internos/', payload);
      }

      setLiquidaciones((prev) => prev.map((l) => {
        if (l.id === liqId) return { ...l, costo_interno: res.data };
        return l;
      }));
      setEditando((prev) => ({ ...prev, _id: res.data.id }));
      toast.success('Costos guardados.');
    } catch (err) {
      const msg = err.response?.data;
      toast.error(typeof msg === 'string' ? msg : 'No se pudieron guardar los costos.');
    } finally {
      setGuardando(false);
    }
  };

  const inputStyle = { fontSize: '12px', padding: '4px 6px', width: '100%' };
  const labelStyle = { fontSize: '11px', color: 'var(--color-text-secondary)', marginBottom: '2px' };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Total Ingresos</div>
          <div className="mono" style={{ fontSize: '22px', fontWeight: 700 }}>${totales.ingresos.toLocaleString('en', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Total Costos</div>
          <div className="mono" style={{ fontSize: '22px', fontWeight: 700, color: '#D32F2F' }}>${totales.costos.toLocaleString('en', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Utilidad</div>
          <div className="mono" style={{ fontSize: '22px', fontWeight: 700, color: totales.utilidad >= 0 ? '#2E7D32' : '#D32F2F' }}>${totales.utilidad.toLocaleString('en', { minimumFractionDigits: 2 })}</div>
        </div>
        <div className="card" style={{ padding: 'var(--spacing-3)', textAlign: 'center' }}>
          <div className="text-xs text-secondary">Margen</div>
          <div className="mono" style={{ fontSize: '22px', fontWeight: 700, color: totales.margen >= 20 ? '#2E7D32' : '#E65100' }}>{totales.margen.toFixed(1)}%</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
        <h2 style={{ marginBottom: 0 }}>Rentabilidad</h2>
        <div style={{ display: 'flex', gap: 'var(--spacing-3)', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
            <input
              type="text" placeholder="Buscar cliente, guía o casillero..."
              value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              style={{ minWidth: '240px', paddingLeft: '32px' }}
            />
          </div>
          <button className="btn-secondary" onClick={cargar} style={{ whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th style={{ width: '36px' }}></th>
              <th>Casillero</th>
              <th>Cliente</th>
              <th>Guía / Tracking</th>
              <th>Peso</th>
              <th>Fecha</th>
              <th className="text-right">Ingresos</th>
              <th className="text-right">Costos</th>
              <th className="text-right">Utilidad</th>
              <th className="text-right">Margen</th>
              <th>Cobro</th>
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan={11} className="text-secondary">Cargando...</td></tr>
            ) : filtradas.length === 0 ? (
              <tr><td colSpan={11} className="text-secondary">No hay liquidaciones.</td></tr>
            ) : paginadas.map((liq) => {
              const ci = liq.costo_interno;
              const ingreso = D2(liq.total);
              const costo = ci ? D2(ci.total_costo) : 0;
              const util = ci ? D2(ci.utilidad) : ingreso;
              const margen = ci ? D2(ci.margen_pct) : (ingreso > 0 ? 100 : 0);
              const isOpen = expandido === liq.id;

              return (
                <React.Fragment key={liq.id}>
                  <tr
                    style={{ cursor: 'pointer', background: isOpen ? 'var(--color-bg-secondary)' : undefined }}
                    onClick={() => toggleExpandir(liq.id)}
                  >
                    <td>
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </td>
                    <td className="mono">{liq.cliente_detalle?.codigo_casillero || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{liq.cliente_nombre}</td>
                    <td className="text-sm">{liq.guia_display || '—'}</td>
                    <td className="text-sm mono">{liq.peso_facturable ? `${D2(liq.peso_facturable)} lb` : '—'}</td>
                    <td className="text-sm text-secondary">{formatFecha(liq.fecha)}</td>
                    <td className="text-right mono" style={{ fontWeight: 600 }}>${ingreso.toFixed(2)}</td>
                    <td className="text-right mono" style={{ color: '#D32F2F' }}>${costo.toFixed(2)}</td>
                    <td className="text-right mono" style={{ fontWeight: 600, color: util >= 0 ? '#2E7D32' : '#D32F2F' }}>${util.toFixed(2)}</td>
                    <td className="text-right">
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
                        background: margen >= 30 ? '#E8F5E9' : margen >= 15 ? '#FFF3E0' : '#FFEBEE',
                        color: margen >= 30 ? '#2E7D32' : margen >= 15 ? '#E65100' : '#D32F2F',
                      }}>
                        {margen.toFixed(1)}%
                      </span>
                    </td>
                    <td className="text-sm">{liq.forma_cobro || '—'}</td>
                  </tr>
                  {isOpen && (
                    <tr>
                      <td colSpan={11} style={{ padding: 'var(--spacing-4)', background: 'var(--color-bg-secondary)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--spacing-4)' }}>
                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Costos del operador</h4>
                            <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                              {[
                                ['Costo MTC', 'costo_mtc'],
                                ['Última milla', 'ultima_milla'],
                                ['Flete volumétrico', 'flete_volumetrico'],
                                ['Consolidación', 'consolidacion_costo'],
                                ['Reembolso aduana', 'reembolso_aduana_costo'],
                                ['Gastos bancarios', 'reembolso_gastos_bancarios_costo'],
                              ].map(([label, key]) => (
                                <div key={key}>
                                  <div style={labelStyle}>{label}</div>
                                  <input
                                    type="number" step="0.01" style={inputStyle}
                                    value={editando[key] ?? ''} onChange={(e) => setEditando((p) => ({ ...p, [key]: e.target.value }))}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Facturas</h4>
                            <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                              {[
                                ['Fac. MTC Servicio', 'fac_mtc_servicio'],
                                ['Fac. MTC Reembolso', 'fac_mtc_reembolso'],
                                ['Fac. Ecuafly Servicio', 'fac_ecuafly_servicio'],
                                ['Fac. Ecuafly Reembolso', 'fac_ecuafly_reembolso'],
                                ['Forma cobro interna', 'forma_cobro_interna'],
                              ].map(([label, key]) => (
                                <div key={key}>
                                  <div style={labelStyle}>{label}</div>
                                  <input
                                    type="text" style={inputStyle}
                                    value={editando[key] ?? ''} onChange={(e) => setEditando((p) => ({ ...p, [key]: e.target.value }))}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 style={{ marginBottom: 'var(--spacing-3)', fontSize: '13px' }}>Resumen e Ingreso</h4>
                            <div className="card" style={{ padding: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', fontSize: '12px' }}>
                                <span className="text-secondary">Subtotal servicios:</span><span className="mono text-right">${D2(liq.subtotal_servicios).toFixed(2)}</span>
                                <span className="text-secondary">IVA:</span><span className="mono text-right">${D2(liq.total_iva).toFixed(2)}</span>
                                <span className="text-secondary">Reembolsos:</span><span className="mono text-right">${D2(liq.total_reembolsos).toFixed(2)}</span>
                                <span className="text-secondary" style={{ fontWeight: 600 }}>Total ingreso:</span><span className="mono text-right" style={{ fontWeight: 700 }}>${ingreso.toFixed(2)}</span>
                              </div>
                            </div>
                            <div>
                              <div style={labelStyle}>Notas</div>
                              <textarea
                                rows={3} style={{ ...inputStyle, resize: 'vertical' }}
                                value={editando.notas ?? ''} onChange={(e) => setEditando((p) => ({ ...p, notas: e.target.value }))}
                              />
                            </div>
                            <button
                              className="btn-primary" style={{ marginTop: 'var(--spacing-3)', width: '100%' }}
                              onClick={(e) => { e.stopPropagation(); guardarCostos(liq.id); }}
                              disabled={guardando}
                            >
                              <Save size={14} /> {guardando ? 'Guardando...' : 'Guardar costos'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtradas.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-4)', flexWrap: 'wrap', gap: 'var(--spacing-3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">Mostrar</span>
            <select value={porPagina} onChange={(e) => setPorPagina(Number(e.target.value))} style={{ width: 'auto', minWidth: '70px' }}>
              {[10, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-secondary">por página</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span className="text-sm text-secondary">
              {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, filtradas.length)} de {filtradas.length}
            </span>
            <button className="btn-ghost btn-sm" disabled={paginaActual <= 1} onClick={() => setPagina(paginaActual - 1)}>
              <ChevronLeft size={16} />
            </button>
            <button className="btn-ghost btn-sm" disabled={paginaActual >= totalPaginas} onClick={() => setPagina(paginaActual + 1)}>
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
